import { Card, Center, Group, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconChartPieOff, IconMinus, IconStar } from '@tabler/icons-react';
import SummaryCardsIcons from '../../constants/SummaryCardsIcons';
import prettifiedLabel from '../../constants/labelPrettifier';
import { changeTone, formatChange } from '../../helpers/atAGlance';
import DashboardsCommon from '../DashboardsCommon';

// Same arrows and colours the rest of the dashboard uses for a delta.
const TONES = {
  up: { color: 'var(--mantine-color-success-5)', Icon: IconArrowUp },
  down: { color: 'var(--mantine-color-danger-5)', Icon: IconArrowDown },
  neutral: { color: 'var(--mantine-color-dimmed)', Icon: IconMinus },
};

/**
 * A ranked breakdown of one metric, each row compared against the previous
 * period, over a trend chart.
 *
 * This used to be two ranked lists side by side with "Current" and "Previous"
 * written UNDERNEATH them, so each label read as belonging to the chart below
 * rather than to the column above it; the lists were padded with blank rows to
 * keep their heights equal; and comparing a row against itself meant finding it
 * twice and subtracting by eye. One list carrying both figures and the change
 * does the same job and is read across instead of diffed.
 *
 * The donut went with them. It partitioned the same values the list already
 * names, so it spent a quarter of the card restating the column beside it — and
 * on a breakdown whose largest entry is routinely a hundred times its smallest,
 * its tail segments are slivers.
 */
const SummaryTopCard = ({
  stats,
  chart,
  currentPeriodData,
  previousPeriodData,
  badgeName,
  numElements,
  currency,
  icon,
}) => {
  if (!currentPeriodData || !previousPeriodData) return null;

  const formatOptions = currency ? { style: 'currency', currency } : {};
  const format = (value) => (value || 0).toLocaleString(navigator.language, formatOptions);

  const withValues = (data) => (data || []).filter((item) => item.value && item.value > 0);
  const hasAnyData = withValues(currentPeriodData).length > 0 || withValues(previousPeriodData).length > 0;

  const currentById = new Map((currentPeriodData || []).map((item) => [item.id, item.value]));
  const previousById = new Map((previousPeriodData || []).map((item) => [item.id, item.value]));

  // Ranked by the CURRENT period, so the order does not shuffle when the
  // comparison range changes. An entry that only had a value previously still
  // gets a row, or a metric that fell to zero would vanish rather than read as
  // the drop it is.
  //
  // Rows are normalised to {id, label, value, previousValue} rather than kept as
  // whichever period's raw object they were sourced from — a row appended from
  // previousPeriodData otherwise carries the PREVIOUS value under `.value`, so a
  // source that earned money last period and nothing this period would render
  // last period's figure in the Current column with a fabricated 0% change.
  const rows = [
    ...withValues(currentPeriodData).sort((a, b) => b.value - a.value),
    ...withValues(previousPeriodData)
      .filter((item) => !(currentPeriodData || []).some((current) => current.id === item.id && current.value > 0))
      .sort((a, b) => b.value - a.value),
  ]
    .slice(0, numElements)
    .map((item) => ({
      id: item.id,
      label: item.label,
      value: currentById.get(item.id) || 0,
      previousValue: previousById.get(item.id) || 0,
    }));

  const chartContent =
    chart === 'line' ? (
      <DashboardsCommon stats={stats} currency={currency} lineStacked={false} expand={true} type={'row'} />
    ) : null;

  const tablerIcon = icon ? SummaryCardsIcons[icon]?.tablerIcon : <IconStar size={16} />;

  return (
    <Card padding="md" radius="md" withBorder h="100%">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          {tablerIcon}
          <Text fw={600}>{badgeName}</Text>
        </Group>

        {hasAnyData ? (
          <Table.ScrollContainer minWidth={360}>
            <Table striped highlightOnHover aria-label={badgeName}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th />
                  <Table.Th ta="right">Current</Table.Th>
                  <Table.Th ta="right">Previous</Table.Th>
                  <Table.Th ta="right">Change</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => {
                  const tone = TONES[changeTone(row.value, row.previousValue)];

                  return (
                    <Table.Tr key={row.id}>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          {SummaryCardsIcons[row.id]?.tablerIcon || SummaryCardsIcons['default'].tablerIcon}
                          <Tooltip label={prettifiedLabel(row.label)} position="top">
                            <Text size="sm" truncate style={{ maxWidth: 220 }}>
                              {prettifiedLabel(row.label)}
                            </Text>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" fw={600}>
                          {format(row.value)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" c="dimmed">
                          {format(row.previousValue)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <tone.Icon size={14} style={{ color: tone.color }} />
                          <Text size="xs" c={tone.color}>
                            {formatChange(row.value, row.previousValue)}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconChartPieOff size={48} color="var(--mantine-color-dimmed)" stroke={1.5} />
              <Text c="dimmed" size="sm">
                No Data
              </Text>
            </Stack>
          </Center>
        )}

        {hasAnyData && chartContent}
      </Stack>
    </Card>
  );
};

export default SummaryTopCard;
